import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ClockIcon, TrashIcon, FunnelIcon, XMarkIcon, EyeIcon } from '@heroicons/react/24/outline';
import { usePermission } from '../../../shared/hooks/usePermission';
import { ActivityLogService } from '../../../shared/services/activityLogService';

const ACTION_OPTIONS = ['CREATED', 'UPDATED', 'DELETED', 'EXPORTED'];
const USER_TYPE_OPTIONS = ['ADMIN', 'PARENT', 'MEDICAL_STAFF'];

const ACTION_BADGE = {
  CREATED: 'bg-green-100 text-green-700',
  UPDATED: 'bg-yellow-100 text-yellow-700',
  DELETED: 'bg-red-100 text-red-700',
  EXPORTED: 'bg-blue-100 text-blue-700',
};

const USER_TYPE_BADGE = {
  ADMIN: 'bg-blue-100 text-blue-700',
  PARENT: 'bg-green-100 text-green-700',
  MEDICAL_STAFF: 'bg-purple-100 text-purple-700',
};

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  const month = d.toLocaleString('en-US', { month: 'short' });
  const day = String(d.getDate()).padStart(2, '0');
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${month} ${day}, ${year} ${hours}:${minutes}`;
}

function truncateId(id) {
  if (!id) return '—';
  const str = String(id);
  return str.length > 8 ? `${str.slice(0, 8)}...` : str;
}

const EMPTY_FILTERS = {
  module: '',
  action: '',
  userType: '',
  startDate: '',
  endDate: '',
  search: '',
};

export default function ActivityLogsPage() {
  const canRead = usePermission('activity-logs', 'read');
  const canDelete = usePermission('activity-logs', 'delete');

  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [moduleOptions, setModuleOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [viewLog, setViewLog] = useState(null);

  const searchDebounceRef = useRef(null);

  // Load module options once
  useEffect(() => {
    if (!canRead) return;
    ActivityLogService.getModuleOptions()
      .then((res) => {
        const data = res.data?.data || res.data || [];
        setModuleOptions(Array.isArray(data) ? data : []);
      })
      .catch(() => setModuleOptions([]));
  }, [canRead]);

  const fetchLogs = useCallback(async (currentFilters, currentPage) => {
    setLoading(true);
    setError('');
    try {
      const params = {
        page: currentPage,
        limit: 20,
        ...(currentFilters.module && { module: currentFilters.module }),
        ...(currentFilters.action && { action: currentFilters.action }),
        ...(currentFilters.userType && { userType: currentFilters.userType }),
        ...(currentFilters.startDate && { startDate: currentFilters.startDate }),
        ...(currentFilters.endDate && { endDate: currentFilters.endDate }),
        ...(currentFilters.search && { search: currentFilters.search }),
      };
      const res = await ActivityLogService.getAll(params);
      setLogs(res.data?.data || []);
      setPagination(res.data?.pagination || { page: 1, limit: 20, total: 0, pages: 1 });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load activity logs');
    } finally {
      setLoading(false);
    }
  }, []);

  // Re-fetch when page changes (filters are debounced separately)
  useEffect(() => {
    if (!canRead) return;
    fetchLogs(filters, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, canRead]);

  const handleFilterChange = (key, value) => {
    const updated = { ...filters, [key]: value };
    setFilters(updated);

    if (key === 'search') {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = setTimeout(() => {
        setPage(1);
        fetchLogs(updated, 1);
      }, 300);
    } else {
      setPage(1);
      fetchLogs(updated, 1);
    }
  };

  const handleClearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setPage(1);
    fetchLogs(EMPTY_FILTERS, 1);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await ActivityLogService.delete(deleteConfirm);
      setDeleteConfirm(null);
      fetchLogs(filters, page);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete log entry');
      setDeleteConfirm(null);
    } finally {
      setDeleting(false);
    }
  };

  if (!canRead) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <ClockIcon className="w-12 h-12 text-gray-300 mb-3" />
        <h2 className="text-lg font-semibold text-gray-700 mb-1">Access Denied</h2>
        <p className="text-sm text-gray-400">You do not have permission to view activity logs.</p>
      </div>
    );
  }

  const hasActiveFilter = Object.values(filters).some((v) => v !== '');

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <ClockIcon className="w-7 h-7 text-blue-600" />
        <h1 className="text-xl font-semibold text-gray-900">Activity Logs</h1>
      </div>

      {/* Filter Bar */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <FunnelIcon className="w-4 h-4 text-gray-400" />
          <span className="text-sm font-medium text-gray-600">Filters</span>
          {hasActiveFilter && (
            <button
              onClick={handleClearFilters}
              className="ml-auto flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 px-2 py-1 rounded hover:bg-gray-100 transition-colors"
            >
              <XMarkIcon className="w-3.5 h-3.5" />
              Clear Filters
            </button>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {/* Module */}
          <select
            value={filters.module}
            onChange={(e) => handleFilterChange('module', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Modules</option>
            {moduleOptions.map((m) => (
              <option key={m.value || m} value={m.value || m}>
                {m.label || m}
              </option>
            ))}
          </select>

          {/* Action */}
          <select
            value={filters.action}
            onChange={(e) => handleFilterChange('action', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Actions</option>
            {ACTION_OPTIONS.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>

          {/* User Type */}
          <select
            value={filters.userType}
            onChange={(e) => handleFilterChange('userType', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All User Types</option>
            {USER_TYPE_OPTIONS.map((t) => (
              <option key={t} value={t}>{t.replace('_', ' ')}</option>
            ))}
          </select>

          {/* Start Date */}
          <input
            type="date"
            value={filters.startDate}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />

          {/* End Date */}
          <input
            type="date"
            value={filters.endDate}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />

          {/* Search */}
          <input
            type="text"
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            placeholder="Search..."
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left px-4 py-3 font-medium text-gray-600">User</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">User Type</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Action</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Module</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Record ID</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Date</th>
                <th className="text-center px-4 py-3 font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    {Array.from({ length: 7 }).map((__, j) => (
                      <td key={j} className="px-4 py-3">
                        <div className="h-4 bg-gray-100 rounded w-full" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-gray-400">
                    No activity logs found
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50/50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-800">{log.userName || log.userId || '—'}</div>
                      {log.userEmail && (
                        <div className="text-xs text-gray-400">{log.userEmail}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          USER_TYPE_BADGE[log.userType] || 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {log.userType ? log.userType.replace('_', ' ') : '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          ACTION_BADGE[log.action] || 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {log.action || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                        {log.module || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-500">
                      {truncateId(log.recordId)}
                    </td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{formatDate(log.createdAt)}</td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => setViewLog(log)}
                          className="p-1.5 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded transition-colors"
                          title="View details"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                        {canDelete && (
                          <button
                            onClick={() => setDeleteConfirm(log.id)}
                            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                            title="Delete log entry"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && pagination.pages > 1 && (
          <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
            <p className="text-xs text-gray-400">
              Showing {((pagination.page - 1) * pagination.limit) + 1}–
              {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} entries
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-2 py-1 text-xs text-gray-600 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Prev
              </button>
              {Array.from({ length: pagination.pages }).map((_, i) => {
                const pageNum = i + 1;
                // Show first, last, and pages near current
                const showPage =
                  pageNum === 1 ||
                  pageNum === pagination.pages ||
                  Math.abs(pageNum - page) <= 2;
                const showEllipsis =
                  (pageNum === 2 && page > 4) ||
                  (pageNum === pagination.pages - 1 && page < pagination.pages - 3);
                if (!showPage && !showEllipsis) return null;
                if (showEllipsis) {
                  return <span key={pageNum} className="px-1 text-gray-400 text-xs">…</span>;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={`w-7 h-7 text-xs rounded transition-colors ${
                      page === pageNum
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-600 border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
                disabled={page >= pagination.pages}
                className="px-2 py-1 text-xs text-gray-600 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* View Detail Modal */}
      {viewLog && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-lg w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-900">Activity Log Details</h3>
              <button onClick={() => setViewLog(null)} className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3 text-sm">
              {[
                { label: 'User', value: viewLog.userName || viewLog.userId },
                { label: 'Email', value: viewLog.userEmail },
                { label: 'User Type', value: viewLog.userType?.replace('_', ' ') },
                { label: 'Action', value: viewLog.action },
                { label: 'Module', value: viewLog.module },
                { label: 'Record ID', value: viewLog.recordId },
                { label: 'Description', value: viewLog.description },
                { label: 'Date', value: formatDate(viewLog.createdAt) },
              ].map(({ label, value }) => value ? (
                <div key={label} className="flex gap-3">
                  <span className="w-28 flex-shrink-0 text-gray-500 font-medium">{label}</span>
                  <span className="text-gray-800 break-all">{value}</span>
                </div>
              ) : null)}
            </div>
            <div className="mt-5 flex justify-end">
              <button
                onClick={() => setViewLog(null)}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
            <h3 className="text-base font-semibold text-gray-900 mb-2">Delete Log Entry</h3>
            <p className="text-sm text-gray-500 mb-5">
              Are you sure you want to delete this activity log entry? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleting}
                className="px-4 py-2 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
